import { useEffect, useMemo, useState } from "react";
import { useEnterEvent } from "../../../context/EnterEvent";

export const DivisionsTable = ({ showRules }) => {
  const { selectedEvent, currency, enrollData } = useEnterEvent();

  const divisionDiscountAmount = Number(enrollData?.divisionDiscountAmount || 0);
  const countryDiscountAmount = Number(enrollData?.countryDiscountAmount || 0);
  const grossDivisionsTotal = Number(enrollData?.divisionsTotalFee || 0);
  const hasDiscounts = divisionDiscountAmount > 0 || countryDiscountAmount > 0;
  const netDivisionsTotal = Number(
    enrollData?.netDivisionsTotalFee ?? Math.max(grossDivisionsTotal - countryDiscountAmount, 0)
  );
  const countryDiscountLabel = enrollData?.countryDiscountLabel;

  const hasPhotos = useMemo(
    () =>
      selectedEvent?.photoFee &&
      selectedEvent?.photoAvailable === "yes" &&
      selectedEvent?.photoInclude === "no",
    [selectedEvent?.photoFee, selectedEvent?.photoAvailable, selectedEvent?.photoInclude]
  );


  let eventDivisionsWithFee = selectedEvent?.divisions?.filter(({ selected }) => selected)?.map((d) => ({
    ...enrollData?.division?.find(({ eventDivisionId: id }) => id === d.id),
    ...d,
  }))

  return (
    <div className="card shadow-xss w-100 border-0 p-0 mb-3 mt-2">
      <div className="card-body">
        <div>
          <div className="row">
            <div className="col">
              <strong>Division</strong>
            </div>
            {showRules ? (
              <div className="col hidden-sm">
                <strong>Rules</strong>
              </div>
            ) : null}
            {hasPhotos ? (
              <div className="col">
                <strong>Purchase Photos</strong>
              </div>
            ) : null}
            <div className="col hidden-sm">
              <strong>Entry Fee</strong>
            </div>
            <div className="col text-right hidden-sm amount-col">
              <strong>Amount</strong>
            </div>
            {showRules ? null : <div className="col hidden-sm" />}
          </div>
          {eventDivisionsWithFee?.map((division, i) => (
            <Row
              key={`division-table-${i}`}
              division={division}
              hasPhotos={hasPhotos}
              showRules={showRules}
              enrolledDivision={enrollData?.division?.find(
                ({ eventDivisionId: id }) => id === division?.id
              )}
            />
          ))}
        </div>
        {showRules ? null : (
          <>
            <div className="row event-select-th">
              <div className="col" />
              {hasPhotos ? <div className="col" /> : null}
              <div className="col" />
              <div className="col sm-no-col text-right">
                <strong>
                  Sub Total:
                  <span
                    className="notranslate"
                    style={hasDiscounts ? { textDecoration: "line-through", opacity: 0.65 } : {}}
                  >
                    {currency}
                    {parseFloat(parseFloat(grossDivisionsTotal || 0).toFixed(2)).toLocaleString("en-US")}
                  </span>
                </strong>
              </div>
              <div className="col" />
            </div>

            {divisionDiscountAmount > 0 ? (
              <div className="row event-select-th">
                <div className="col" />
                {hasPhotos ? <div className="col" /> : null}
                <div className="col" />
                <div className="col sm-no-col text-right">
                  Division discount
                  <span className="notranslate text-success" style={{ whiteSpace: "nowrap", marginLeft: 6 }}>
                    {`-${currency}${parseFloat(parseFloat(divisionDiscountAmount || 0).toFixed(2)).toLocaleString("en-US")}`}
                  </span>
                </div>
                <div className="col" />
              </div>
            ) : null}

            {countryDiscountAmount > 0 ? (
              <div className="row event-select-th">
                <div className="col" />
                {hasPhotos ? <div className="col" /> : null}
                <div className="col" />
                <div className="col sm-no-col text-right">
                  <i className="fa-solid fa-location-dot mr-1"></i>
                  Country discount{countryDiscountLabel ? ` (${countryDiscountLabel})` : ""}
                  <span className="notranslate text-success" style={{ whiteSpace: "nowrap", marginLeft: 6 }}>
                    {`-${currency}${parseFloat(parseFloat(countryDiscountAmount || 0).toFixed(2)).toLocaleString("en-US")}`}
                  </span>
                </div>
                <div className="col" />
              </div>
            ) : null}

            {hasDiscounts ? (
              <div className="row event-select-th">
                <div className="col" />
                {hasPhotos ? <div className="col" /> : null}
                <div className="col" />
                <div className="col sm-no-col text-right">
                  <strong>
                    Total:
                    <span className="notranslate">
                      {currency}
                      {parseFloat(parseFloat(netDivisionsTotal || 0).toFixed(2)).toLocaleString("en-US")}
                    </span>
                  </strong>
                </div>
                <div className="col" />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

const Row = ({ division, enrolledDivision, hasPhotos, showRules }) => {
  const {
    selectedEvent,
    withdrawDivision,
    enrollData,
    setEnrollData,
    currency,
  } = useEnterEvent();

  const handlePhotoChange = (e) => {
    const isChecked = e.target.checked;
    const i = enrollData?.division?.findIndex(
      ({ eventDivisionId: id }) => id === division?.id
    );
    if (i >= 0) {
      if (isChecked) {
        enrollData.division[i].photoFee = selectedEvent?.photoFee;
      } else {
        delete enrollData.division[i].photoFee;
      }

      setEnrollData({
        ...enrollData,
      });
    }
  };

  const { divisionDesc, divisionName, id, name } = division;
  const rowDivisionDiscount = Number(enrolledDivision?.divisionDiscountAmount || 0);
  const showDivisionDiscountBadge = rowDivisionDiscount > 0;

  return (
    <div key={`division-row-${id}`} className="row mb-3 mt-2 mobile--selected-division">
      <div className="col">
        <div>{name || divisionDesc}</div>
        {showDivisionDiscountBadge ? (
          <span
            className="d-inline-flex align-items-center mt-1 px-2 py-1"
            style={{
              background: "#d4f0d7",
              color: "#0a6a27",
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            <i className="fa-solid fa-tag mr-1"></i>
            Discount {currency}
            {parseFloat(parseFloat(rowDivisionDiscount || 0).toFixed(2)).toLocaleString("en-US")} off
          </span>
        ) : null}
      </div>
      {showRules ? (
        <div className="col hidden-sm">{divisionName || divisionDesc}</div>
      ) : null}{" "}
      {/*Will add rules here*/}
      {hasPhotos ? (
        <div className="col">
          {showRules ? (
            <div className="col">
              {currency}
              {(enrolledDivision?.photoFee ? selectedEvent?.photoFee : 0)?.toLocaleString("en-US")}
            </div>
          ) : (
            <label className="toggle toggle-menu-color  d-flex left-line-toggle">
              <span className="mr-1">
                <input
                  // defaultChecked={!!enrolledDivision?.photoFee}
                  checked={!!enrolledDivision?.photoFee}
                  onChange={handlePhotoChange}
                  type="checkbox"
                />
                <span className="toggle-icon"></span>
              </span>
              <span className=" font-xssss mont-font fw-700">
                {currency}
                {selectedEvent?.photoFee?.toLocaleString("en-US")}
              </span>
            </label>
          )}
        </div>
      ) : null}
      <ShowPrice isChecked={!!enrolledDivision?.photoFee} divisionId={id} />
      {showRules ? null : (
        <div className="col">
          <button
            onClick={() => withdrawDivision(id)}
            className="form-control text-center small-btn text-white bg-red-gradiant border-0 p-0 font-xsssss"
          >
            <i className="fa-solid fa-circle-xmark"></i> Withdraw
          </button>
        </div>
      )}
    </div>
  );
};

const ShowPrice = ({ divisionId }) => {
  const { enrollData, currency } = useEnterEvent();

  const [selectedDivision, setSelectedDivision] = useState();

  useEffect(() => {
    setSelectedDivision(
      enrollData?.division?.find(({ eventDivisionId: id }) => id === divisionId)
    );
  }, [enrollData, divisionId]);

  const {
    entryFee = 0,
    photoFee = 0,
    lateFee = 0,
    baseEntryFee = 0,
    divisionDiscountAmount = 0,
    finalEntryFee = null,
  } = selectedDivision || {};

  const hasDivisionDiscount = Number(divisionDiscountAmount || 0) > 0;
  const effectiveEntryFee = Number((finalEntryFee ?? (lateFee || entryFee)) || 0);
  const originalEntryFee = Number(baseEntryFee || (lateFee || entryFee) || effectiveEntryFee || 0);
  const originalAmount = Number(photoFee || 0) + originalEntryFee;
  const finalAmount = Number(photoFee || 0) + effectiveEntryFee;

  return (
    <>
      <div className="col sm-no-col sm-pt-1">
        <b className="show-sm">Entry Fee : </b>
        <span className="notranslate">
          {currency}
          {effectiveEntryFee?.toLocaleString("en-US")}
        </span>
      </div>
      <div className="col text-right sm-pb-1 sm-no-col sm-text-left">
        <b className="show-sm">Amount : </b>
        {hasDivisionDiscount ? (
          <>
            <div className="notranslate" style={{ textDecoration: "line-through", opacity: 0.65 }}>
              {currency}
              {originalAmount?.toLocaleString("en-US")}
            </div>
            <div className="notranslate text-success fw-700">
              {currency}
              {finalAmount?.toLocaleString("en-US")}
            </div>
          </>
        ) : (
          <span className="notranslate">
            {currency}
            {(photoFee + effectiveEntryFee)?.toLocaleString("en-US")}
          </span>
        )}
      </div>
    </>
  );
};
